import { useState, useEffect, useCallback } from "react";
import { Loader2, Plus, Search, RefreshCw, Flame, X, Sparkles } from "lucide-react";
import api, { apiErr } from "../../lib/api";
import { toast } from "sonner";
import GrowthCard from "./GrowthCard";
import GrowthDrawer from "./GrowthDrawer";
import GrowthCloseModal from "./GrowthCloseModal";
import GrowthStats from "./GrowthStats";
import GrowthApiKeys from "./GrowthApiKeys";
import {
  COLUMNS, COLUMN_LABELS, SOURCES, SOURCE_LABELS, PRIORITIES, PRIORITY_LABELS,
  TYPES, TYPE_LABELS,
} from "./growthConstants";

// Módulo de captación de usuarios — tablero de 5 columnas fijas (NEW..CLOSED). Sigue el mismo
// patrón de drag & drop nativo HTML5 que MarketingKanbanTab (Admin.jsx), sin librería nueva:
// arrastrar sobre otra tarjeta inserta antes de ella (reordena dentro de columna), arrastrar
// sobre el hueco vacío de la columna añade al final. Toda la posición se persiste en backend
// (POST /growth/cards/{id}/move) — nunca solo en memoria del navegador.
export default function GrowthBoard() {
  const [board, setBoard] = useState(null);
  const [stats, setStats] = useState(null);
  const [dragId, setDragId] = useState(null);
  const [selected, setSelected] = useState(null);
  const [pendingClose, setPendingClose] = useState(null); // { id, position }
  const [closing, setClosing] = useState(false);
  const [showApiKeys, setShowApiKeys] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [search, setSearch] = useState("");
  const [filterSource, setFilterSource] = useState("");
  const [filterPriority, setFilterPriority] = useState("");

  const load = useCallback(() => {
    api.get("/growth/board").then(({ data }) => setBoard(data)).catch((e) => toast.error(apiErr(e)));
    api.get("/growth/stats").then(({ data }) => setStats(data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const openCard = async (id) => {
    try {
      const { data } = await api.get(`/growth/cards/${id}`);
      setSelected(data);
    } catch (e) { toast.error(apiErr(e)); }
  };

  const doMove = async (id, column, position) => {
    try {
      await api.post(`/growth/cards/${id}/move`, { column, position });
      load();
      if (selected?.id === id) openCard(id);
    } catch (e) { toast.error(apiErr(e)); }
  };

  const handleDrop = (column, position) => {
    if (!dragId) return;
    const id = dragId;
    setDragId(null);
    if (column === "CLOSED") { setPendingClose({ id, position }); return; }
    doMove(id, column, position);
  };

  const confirmClose = async (reason) => {
    if (!pendingClose) return;
    setClosing(true);
    try {
      await api.patch(`/growth/cards/${pendingClose.id}`, { closeReason: reason });
      await api.post(`/growth/cards/${pendingClose.id}/move`, { column: "CLOSED", position: pendingClose.position });
      setPendingClose(null);
      load();
    } catch (e) { toast.error(apiErr(e)); } finally { setClosing(false); }
  };

  const patchSelected = async (patch) => {
    try {
      const { data } = await api.patch(`/growth/cards/${selected.id}`, patch);
      setSelected((prev) => ({ ...prev, ...data }));
      load();
    } catch (e) { toast.error(apiErr(e)); }
  };

  const moveSelected = (column) => {
    if (column === "CLOSED") { setPendingClose({ id: selected.id, position: 999999 }); return; }
    doMove(selected.id, column, 999999); // el backend clampa: 999999 siempre acaba al final
  };

  if (!board) return <div className="py-16 text-center"><Loader2 className="mx-auto animate-spin text-red-600" size={28} /></div>;

  const q = search.trim().toLowerCase();
  const matches = (c) => {
    if (filterSource && c.source !== filterSource) return false;
    if (filterPriority && c.priority !== filterPriority) return false;
    if (!q) return true;
    return [c.title, c.sourceUser, c.summary, c.whyRelevant, c.notes].some(
      (v) => v && v.toLowerCase().includes(q));
  };

  return (
    <div className="space-y-4" data-testid="growth-board">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-slate-500">
          Encontrar → decidir → contactar → seguir → registrar → convertir. Arrastra tarjetas entre columnas.
        </p>
        <div className="flex items-center gap-2">
          <button onClick={() => toast.info("Todavía no está conectado — de momento, añade oportunidades a mano o por API.")}
            data-testid="growth-search-now" title="Preparado para la integración futura con ChatGPT (sección 33 del spec)"
            className="flex items-center gap-1.5 rounded-lg border border-slate-200 px-3 py-1.5 text-sm font-medium text-slate-500 hover:bg-slate-50">
            <Sparkles size={15} /> Buscar oportunidades ahora
          </button>
          <button onClick={() => setShowApiKeys((v) => !v)} data-testid="growth-toggle-api-keys"
            className="rounded-lg border border-slate-200 px-3 py-1.5 text-sm font-medium text-slate-600 hover:bg-slate-50">
            API keys
          </button>
          <button onClick={load} className="rounded-lg border border-slate-200 p-1.5 text-slate-500 hover:bg-slate-50"><RefreshCw size={15} /></button>
          <button onClick={() => setShowCreate(true)} data-testid="growth-create-btn"
            className="flex items-center gap-1.5 rounded-lg bg-red-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-red-700">
            <Plus size={15} /> Oportunidad
          </button>
        </div>
      </div>

      {showApiKeys && <GrowthApiKeys />}

      <GrowthStats stats={stats} />

      {board.today?.length > 0 && (
        <div className="rounded-xl border-2 border-red-200 bg-red-50/60 p-3" data-testid="growth-today">
          <h3 className="mb-2 flex items-center gap-1.5 text-sm font-bold uppercase tracking-wide text-red-700">
            <Flame size={15} /> Prioridad de hoy
          </h3>
          <div className="flex flex-wrap gap-2">
            {board.today.map((c) => (
              <button key={c.id} onClick={() => openCard(c.id)}
                className="rounded-lg border border-red-200 bg-white px-2.5 py-1.5 text-left text-xs hover:border-red-400">
                <div className="font-semibold text-slate-800 line-clamp-1 max-w-[220px]">{c.title}</div>
                <div className="text-red-600">{c.todayReason}</div>
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2">
        <div className="relative min-w-[200px] flex-1">
          <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-300" />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Buscar título, usuario, resumen..."
            data-testid="growth-search" className="w-full rounded-lg border border-slate-200 py-1.5 pl-8 pr-3 text-sm" />
        </div>
        <select value={filterSource} onChange={(e) => setFilterSource(e.target.value)}
          className="rounded-lg border border-slate-200 px-2 py-1.5 text-sm">
          <option value="">Todas las fuentes</option>
          {SOURCES.map((s) => <option key={s} value={s}>{SOURCE_LABELS[s]}</option>)}
        </select>
        <select value={filterPriority} onChange={(e) => setFilterPriority(e.target.value)}
          className="rounded-lg border border-slate-200 px-2 py-1.5 text-sm">
          <option value="">Toda prioridad</option>
          {PRIORITIES.map((p) => <option key={p} value={p}>{PRIORITY_LABELS[p]}</option>)}
        </select>
      </div>

      <div className="grid grid-cols-1 gap-3 overflow-x-auto sm:grid-cols-2 lg:grid-cols-5">
        {COLUMNS.map((col) => {
          const cards = (board.columns.find((c) => c.id === col)?.cards || []).filter(matches);
          return (
            <div key={col}
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => { e.preventDefault(); handleDrop(col, cards.length); }}
              className="min-h-[220px] rounded-xl border-2 border-dashed border-slate-200 bg-slate-50/60 p-3">
              <div className="mb-3 flex items-center justify-between">
                <h3 className="text-sm font-bold uppercase tracking-wide text-slate-600">{COLUMN_LABELS[col]}</h3>
                <span className="rounded-full bg-slate-200 px-2 py-0.5 text-xs font-semibold text-slate-600">{cards.length}</span>
              </div>
              <div className="space-y-2">
                {cards.map((c, idx) => (
                  <div key={c.id}
                    onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); }}
                    onDrop={(e) => { e.preventDefault(); e.stopPropagation(); handleDrop(col, idx); }}>
                    <GrowthCard card={c} draggable onDragStart={() => setDragId(c.id)} onClick={() => openCard(c.id)} />
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      {selected && (
        <GrowthDrawer card={selected} onClose={() => setSelected(null)} onPatch={patchSelected} onMove={moveSelected} />
      )}
      {pendingClose && (
        <GrowthCloseModal onClose={() => setPendingClose(null)} onConfirm={confirmClose} busy={closing} />
      )}
      {showCreate && (
        <GrowthCreateModal onClose={() => setShowCreate(false)} onCreated={() => { setShowCreate(false); load(); }} />
      )}
    </div>
  );
}

// Formulario de creación manual — solo los campos imprescindibles (sección 1: "crear
// oportunidades manualmente"); el resto se completa luego desde el drawer. Único caller, así
// que vive aquí en vez de en su propio fichero (YAGNI).
function GrowthCreateModal({ onClose, onCreated }) {
  const [title, setTitle] = useState("");
  const [source, setSource] = useState(SOURCES[0]);
  const [priority, setPriority] = useState("MEDIUM");
  const [type, setType] = useState("OTHER");
  const [sourceUrl, setSourceUrl] = useState("");
  const [summary, setSummary] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!title.trim()) { toast.warning("Escribe un título"); return; }
    setBusy(true);
    try {
      await api.post("/growth/cards", { title, source, priority, type, sourceUrl: sourceUrl || null, summary });
      toast.success("Oportunidad creada.");
      onCreated();
    } catch (e) {
      if (e?.response?.status === 409) toast.warning("Ya existe una oportunidad con esa URL.");
      else toast.error(apiErr(e));
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="w-full max-w-md space-y-3 rounded-2xl bg-white p-5 shadow-2xl">
        <div className="flex items-center justify-between">
          <h3 className="text-base font-bold text-slate-900">Nueva oportunidad</h3>
          <button onClick={onClose} className="rounded-lg p-1 text-slate-400 hover:bg-slate-100"><X size={16} /></button>
        </div>
        <input autoFocus value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Título"
          data-testid="growth-create-title" className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm" />
        <div className="grid grid-cols-2 gap-2">
          <select value={source} onChange={(e) => setSource(e.target.value)} className="rounded-lg border border-slate-200 px-2 py-2 text-sm">
            {SOURCES.map((s) => <option key={s} value={s}>{SOURCE_LABELS[s]}</option>)}
          </select>
          <select value={priority} onChange={(e) => setPriority(e.target.value)} className="rounded-lg border border-slate-200 px-2 py-2 text-sm">
            {PRIORITIES.map((p) => <option key={p} value={p}>{PRIORITY_LABELS[p]}</option>)}
          </select>
        </div>
        <select value={type} onChange={(e) => setType(e.target.value)} className="w-full rounded-lg border border-slate-200 px-2 py-2 text-sm">
          {TYPES.map((t) => <option key={t} value={t}>{TYPE_LABELS[t]}</option>)}
        </select>
        <input value={sourceUrl} onChange={(e) => setSourceUrl(e.target.value)} placeholder="Enlace (opcional)"
          className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm" />
        <textarea value={summary} onChange={(e) => setSummary(e.target.value)} rows={3} placeholder="Resumen (opcional)"
          className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm" />
        <button onClick={submit} disabled={busy} data-testid="growth-create-submit"
          className="flex w-full items-center justify-center gap-1.5 rounded-lg bg-red-600 px-4 py-2 text-sm font-semibold text-white hover:bg-red-700 disabled:opacity-60">
          {busy && <Loader2 size={14} className="animate-spin" />} Crear
        </button>
      </div>
    </div>
  );
}
